import React, { useState } from 'react';
import Card from '../../common/Card';
import Pagination from '../../common/Pagination';
import { useTheme } from '../../../contexts/ThemeContext';

const ClientsList = ({
  clients,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete
}) => {
  const { isDarkMode } = useTheme();
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Truncate text helper
  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get client status
  const getClientStatus = (client) => {
    if (client.login && client.matriculeFiscal) {
      return { status: 'Complet', color: 'green' };
    } else if (client.login || client.matriculeFiscal) {
      return { status: 'Partiel', color: 'yellow' };
    }
    return { status: 'Incomplet', color: 'red' };
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mb-4 ${
                isDarkMode ? 'border-dark-primary-600' : 'border-primary-600'
              }`}></div>
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                Chargement des clients...
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <Card>
        <div className="p-6">
          <div className="text-center py-12">
            <div className={`mx-auto h-24 w-24 rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <i className={`fas fa-users text-2xl ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}></i>
            </div>
            <h3 className={`text-lg font-medium mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Aucun client trouvé
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Commencez par créer votre premier client.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle & Results Info */}
      <Card>
        <div className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className={`flex items-center text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <i className="fas fa-info-circle mr-2 flex-shrink-0"></i>
              <span className="break-words">
                {pagination.total} client{pagination.total > 1 ? 's' : ''} au total
                {pagination.total > pagination.pageSize && (
                  <span className="ml-1 hidden sm:inline">
                    • Page {pagination.page} sur {pagination.maxPage}
                  </span>
                )}
              </span>
            </div>
            
            {/* View Mode Toggle */}
            <div className={`flex rounded-lg p-1 flex-shrink-0 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'cards'
                    ? isDarkMode
                      ? 'bg-dark-primary-600 text-white shadow-sm'
                      : 'bg-white text-primary-600 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-th-large mr-1.5"></i>
                <span className="hidden sm:inline">Cartes</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? isDarkMode
                      ? 'bg-dark-primary-600 text-white shadow-sm'
                      : 'bg-white text-primary-600 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-list mr-1.5"></i>
                <span className="hidden sm:inline">Liste</span>
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {clients.map((client) => {
            const status = getClientStatus(client);
            return (
              <Card key={client.id} className="hover:shadow-lg transition-shadow duration-200">
                <div className="p-4 sm:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center mr-3 flex-shrink-0 overflow-hidden ${
                        isDarkMode ? 'bg-dark-primary-900/50 text-dark-primary-400' : 'bg-primary-100 text-primary-600'
                      }`}>
                        {client.logo ? (
                          <img
                            src={
                              client.logo.startsWith('data:') || client.logo.startsWith('http')
                                ? client.logo
                                : `data:image/png;base64,${client.logo}`
                            }
                            alt={client.raisonSocial || 'Logo'}
                            className="object-contain w-full h-full"
                            style={{ backgroundColor: 'white', padding: '6px' }}
                          />
                        ) : (
                          <i className="fas fa-building text-sm sm:text-lg"></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 
                          className={`font-semibold text-sm sm:text-base break-words line-clamp-2 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}
                          title={client.raisonSocial || 'Nom non défini'}
                        >
                          {truncateText(client.raisonSocial || 'Nom non défini', 25)}
                        </h3>
                        <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ID: {client.id}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="mb-4 space-y-1">
                    {client.login && (
                      <div className={`flex items-center text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <i className={`fas fa-user mr-2 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                        <span 
                          className="font-medium truncate flex-1 min-w-0"
                          title={client.login}
                        >
                          {truncateText(client.login, 20)}
                        </span>
                      </div>
                    )}
                    {client.tel && (
                      <div className={`flex items-center text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <i className={`fas fa-phone mr-2 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                        <span 
                          className="truncate flex-1 min-w-0"
                          title={client.tel}
                        >
                          {truncateText(client.tel, 18)}
                        </span>
                      </div>
                    )}
                    {client.matriculeFiscal && (
                      <div className={`flex items-center text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <i className={`fas fa-file-invoice mr-2 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                        <span 
                          className="truncate flex-1 min-w-0"
                          title={client.matriculeFiscal}
                        >
                          {truncateText(client.matriculeFiscal, 15)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Status & Flags */}
                  <div className="mb-4 space-y-2">

                    {/* Client Flags */}
                    <div className="flex flex-wrap gap-1">
                      {client.timberFiscal && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                          isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                        }`}>
                          <i className="fas fa-stamp mr-1 text-xs"></i>
                          Timber
                        </span>
                      )}
                      {client.assujeti && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                          isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'
                        }`}>
                          <i className="fas fa-check mr-1 text-xs"></i>
                          Assujetti
                        </span>
                      )}
                      {client.exonore && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                          isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'
                        }`}>
                          <i className="fas fa-minus-circle mr-1 text-xs"></i>
                          Exonéré
                        </span>
                      )}
                      {client.annuelle && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                          isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                          <i className="fas fa-calendar mr-1 text-xs"></i>
                          Annuelle
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => onView(client)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        isDarkMode 
                          ? 'bg-dark-primary-900/50 text-dark-primary-300 hover:bg-dark-primary-800/50' 
                          : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                      }`}
                    >
                      <i className="fas fa-eye mr-1"></i>
                      <span className="hidden sm:inline">Voir</span>
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit(client)}
                        className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                          isDarkMode 
                            ? 'bg-amber-900/50 text-amber-300 hover:bg-amber-800/50' 
                            : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                        }`}
                        title="Modifier"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => onDelete(client)}
                        className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                          isDarkMode 
                            ? 'bg-red-900/50 text-red-300 hover:bg-red-800/50' 
                            : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                        title="Supprimer"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Client
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Contact
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Matricule
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                  {clients.map((client) => {
                    const status = getClientStatus(client);
                    return (
                      <tr key={client.id} className={`transition-colors ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}>
                        <td className="px-3 sm:px-6 py-4">
                          <div className="flex items-center min-w-0">
                            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 overflow-hidden ${
                              isDarkMode ? 'bg-dark-primary-900/50 text-dark-primary-400' : 'bg-primary-100 text-primary-600'
                            }`}>
                              {client.logo ? (
                                <img
                                  src={
                                    client.logo.startsWith('data:') || client.logo.startsWith('http')
                                      ? client.logo
                                      : `data:image/png;base64,${client.logo}`
                                  }
                                  alt={client.raisonSocial || 'Logo'}
                                  className="object-contain w-full h-full"
                                  style={{ backgroundColor: 'white', transform: 'scale(1.18)' }}
                                />
                              ) : (
                                <i className="fas fa-building text-xs sm:text-sm"></i>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div 
                                className={`text-xs sm:text-sm font-medium break-words line-clamp-2 ${
                                  isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}
                                title={client.raisonSocial || 'Nom non défini'}
                              >
                                {truncateText(client.raisonSocial || 'Nom non défini', 30)}
                              </div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ID: {client.id}
                              </div>
                              {/* Show contact info on mobile */}
                              <div className="md:hidden text-xs text-gray-500 mt-1">
                                {client.login && (
                                  <div className="truncate" title={client.login}>
                                    <i className="fas fa-user mr-1"></i>
                                    {truncateText(client.login, 20)}
                                  </div>
                                )}
                                {client.tel && (
                                  <div className="truncate" title={client.tel}>
                                    <i className="fas fa-phone mr-1"></i>
                                    {truncateText(client.tel, 15)}
                                  </div>
                                )}
                                <div className="lg:hidden text-xs text-gray-400 mt-1">
                                  {client.matriculeFiscal && truncateText(client.matriculeFiscal, 15)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 hidden md:table-cell">
                          <div className={`text-xs sm:text-sm font-medium break-words ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {client.login && (
                              <div 
                                className="truncate"
                                title={client.login}
                              >
                                <i className={`fas fa-user mr-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                                {truncateText(client.login, 20)}
                              </div>
                            )}
                          </div>
                          {client.tel && (
                            <div 
                              className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                              title={client.tel}
                            >
                              <i className="fas fa-phone mr-1"></i>
                              {truncateText(client.tel, 15)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                          <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {client.matriculeFiscal ? (
                              <span className="font-mono">{client.matriculeFiscal}</span>
                            ) : (
                              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Non défini</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                            <button
                              onClick={() => onView(client)}
                              className={`p-1 rounded text-xs sm:text-sm transition-colors ${
                                isDarkMode 
                                  ? 'text-dark-primary-400 hover:text-dark-primary-300' 
                                  : 'text-primary-600 hover:text-primary-900'
                              }`}
                              title="Voir les détails"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              onClick={() => onEdit(client)}
                              className={`p-1 rounded text-xs sm:text-sm transition-colors ${
                                isDarkMode 
                                  ? 'text-amber-400 hover:text-amber-300' 
                                  : 'text-amber-600 hover:text-amber-900'
                              }`}
                              title="Modifier"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              onClick={() => onDelete(client)}
                              className={`p-1 rounded text-xs sm:text-sm transition-colors ${
                                isDarkMode 
                                  ? 'text-red-400 hover:text-red-300' 
                                  : 'text-red-600 hover:text-red-900'
                              }`}
                              title="Supprimer"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination.maxPage > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.maxPage}
            pageSize={pagination.pageSize}
            totalItems={pagination.total}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default ClientsList;